import { useState } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import Select from "react-select";
import moment from "moment";
import { cn } from "../../../../utils/classnames";
import {
  newBookingStatusOptions,
  newPaymentStatusOptions,
  newBookingTypeOptions,
} from "../../../../utils/dataLists";
import {
  ChevronDownIcon,
  SearchIcon,
  SelectDropdownIndicator,
} from "../../modification-requests/components/Icons";

// Mirrors the filter styling of the modification-requests page, adapted to the
// booking list's multi-select filters (height grows with the selected chips).
const multiSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "45px",
    borderRadius: "5px",
    borderColor: "#D9D9D9",
    boxShadow: "none",
    backgroundColor: "#FFFFFF",
    "&:hover": { borderColor: "#D9D9D9" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
  placeholder: (base) => ({ ...base, color: "#7C7B7B", fontSize: "15px", lineHeight: "22px" }),
  multiValue: (base) => ({ ...base, backgroundColor: "#F1F1F1", borderRadius: "4px" }),
  multiValueLabel: (base) => ({ ...base, color: "#282828", fontSize: "13px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, paddingRight: "15px", color: "#282828" }),
  menu: (base) => ({ ...base, borderRadius: "5px", marginTop: "6px", zIndex: 30 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#F3F3F3" : "#FFFFFF",
    color: "#282828",
    cursor: "pointer",
    fontSize: "15px",
    lineHeight: "22px",
    "&:hover": { backgroundColor: "#F7F7F7" },
  }),
};

const FieldLabel = ({ children }) => (
  <div className="tw:mb-2 tw:text-[14px] tw:leading-5 tw:text-[#7C7B7B]">{children}</div>
);

const MultiSelectField = ({ label, value, onChange, options }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <Select
      isMulti
      value={value}
      onChange={onChange}
      options={options}
      placeholder="Select..."
      isSearchable={false}
      styles={multiSelectStyles}
      components={{ DropdownIndicator: SelectDropdownIndicator, IndicatorSeparator: null }}
    />
  </div>
);

const BookingsFilter = ({
  bookingStatus,
  setBookingStatus,
  setdateRange,
  resetDateRange,
  dateRange,
  setSearch,
  paymentStatus,
  setPaymentStatus,
  bookingType,
  setBookingType,
  setFilter,
  referralsOptions,
  referralsFilter,
  setReferralsFilter,
}) => {
  const [showFilters, setShowFilters] = useState(false);

  const handleCallback = (start, end) => {
    setdateRange({ startDate: start, endDate: end });
  };

  const dateText =
    dateRange?.startDate && dateRange?.endDate
      ? `${moment(dateRange.startDate).format("YYYY/MM/DD")} - ${moment(dateRange.endDate).format("YYYY/MM/DD")}`
      : "";

  const onClearFilters = () => {
    setSearch("");
    setBookingStatus([]);
    setPaymentStatus([]);
    setBookingType([]);
    setReferralsFilter([]);
    resetDateRange();
  };

  return (
    <section className="tw:mt-[32px] tw:flex tw:flex-col tw:gap-2">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <div className="tw:flex tw:h-[45px] tw:w-full tw:max-w-[329px] tw:items-center tw:gap-[10px] tw:rounded-[5px] tw:border tw:border-[#D9D9D9] tw:bg-white tw:pl-[15px] tw:pr-[19px]">
          <SearchIcon />
          <input
            type="text"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search"
            className="tw:h-full tw:w-full tw:border-0 tw:bg-transparent tw:p-0 tw:text-[15px] tw:leading-[22px] tw:text-[#282828] tw:outline-none"
          />
        </div>

        <button
          type="button"
          className="tw:border-0 tw:bg-transparent tw:p-0"
          onClick={() => setShowFilters((prev) => !prev)}
        >
          <span className="tw:flex tw:h-[45px] tw:w-[108px] tw:items-center tw:justify-between tw:rounded-[5px] tw:bg-[#282828] tw:px-[17px] tw:text-white tw:font-hanken-grotesk">
            <span className="tw:text-[15px] tw:leading-[22px]">Filter</span>
            <span className="tw:font-['Noto_Serif_JP'] tw:text-[14px] tw:font-bold">{showFilters ? "-" : "+"}</span>
          </span>
        </button>
      </div>

      {showFilters ? (
        <div className="tw:rounded-[5px] tw:bg-[#F9F9F9] tw:p-5">
          <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2 tw:xl:grid-cols-4">
            <MultiSelectField
              label="Booking Status"
              value={bookingStatus}
              onChange={setBookingStatus}
              options={newBookingStatusOptions}
            />

            <MultiSelectField
              label="Payment Status"
              value={paymentStatus}
              onChange={setPaymentStatus}
              options={newPaymentStatusOptions}
            />

            <MultiSelectField
              label="Booking Type"
              value={bookingType}
              onChange={setBookingType}
              options={newBookingTypeOptions}
            />

            <MultiSelectField
              label="Referrer"
              value={referralsFilter}
              onChange={setReferralsFilter}
              options={referralsOptions}
            />

            <div>
              <FieldLabel>Date・Period</FieldLabel>
              <div className="tw:relative">
                <DateRangePicker onCallback={handleCallback} initialSettings={dateRange}>
                  <input
                    type="text"
                    readOnly
                    value={dateText}
                    placeholder="Select..."
                    className={cn(
                      "tw:h-[45px] tw:w-full tw:rounded-[5px] tw:border tw:border-[#D9D9D9] tw:bg-white tw:px-[15px] tw:pr-[34px] tw:text-[15px] tw:leading-[22px]",
                      dateText ? "tw:!text-[#282828]" : "tw:!text-[#7C7B7B]",
                    )}
                  />
                </DateRangePicker>
                <div className="tw:pointer-events-none tw:absolute tw:right-[15px] tw:top-1/2 tw:-translate-y-1/2">
                  <ChevronDownIcon />
                </div>
              </div>
            </div>
          </div>

          <div className="tw:mt-5 tw:flex tw:items-center tw:gap-2">
            <button
              type="button"
              className="tw:border-0 tw:bg-transparent tw:p-0"
              onClick={() => setFilter()}
            >
              <span className="tw:flex tw:h-[45px] tw:w-[120px] tw:items-center tw:justify-center tw:rounded-[5px] tw:bg-[#282828] tw:text-[15px] tw:font-bold tw:leading-[22px] tw:text-white tw:font-hanken-grotesk">
                Apply
              </span>
            </button>
            <button
              type="button"
              className="tw:border-0 tw:bg-transparent tw:p-0"
              onClick={onClearFilters}
            >
              <span className="tw:flex tw:h-[45px] tw:w-[120px] tw:items-center tw:justify-center tw:rounded-[5px] tw:border tw:border-[#282828] tw:bg-white tw:text-[15px] tw:leading-[22px] tw:text-[#282828] tw:font-hanken-grotesk">
                Clear
              </span>
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default BookingsFilter;
